import type { FC } from 'react';
import { useCallback, useContext, useState } from 'react';
import c from 'classnames';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
// @dynamic-i18n-keys
// Dynamic t() at lines 161, 166, 295, 316:
//   studio.discountModal.form.error.tooMany.title
//   studio.discountModal.form.error.tooMany.title.mobile
//   studio.discountModal.form.error.tooMany.message
//   studio.discountModal.form.error.tooMany.message.mobile
//   studio.discountModal.actionLabel.edit
//   studio.discountModal.actionLabel.create
import { Controller, FormProvider } from 'react-hook-form';
import { URLS, Url } from '@wix/da-url';
import Modal from '@wix/da-ds/pkg/modals/dsPresets/Modal';
import Flex from '@wix/da-ds/pkg/Flex';
import CoreExclusiveFeature from '@wix/da-shared-react/pkg/indicators/CoreExclusiveFeature';
import BuyCoreBrandedButton from '@wix/da-shared-react/pkg/Button/core/dsPresets/BuyCoreBrandedButton';
import TextButton from '@wix/da-ds/pkg/Buttons/TextButton';
import TextInput from '@wix/da-ds/pkg/formControls/dsPresets/TextInput';
import { DiscountFormField } from '../types';
import DiscountDetails, {
  DiscountDetailsData,
} from './_partials/DiscountDetails';
import {
  getCurrentUserHasCore,
  getCoreLevel,
} from '@wix/da-shared-react/pkg/publicSession/selectors';
import DiscountSelector from './_partials/DiscountSelector';
import { useCreateForm } from './hooks';
import AudienceSelector from './_partials/AudienceSelector';
import AppliesToSelector from './_partials/AppliesToSelector';
import StartTimeInput from './_partials/StartTimeInput';
import EndTimeInput from './_partials/EndTimeInput/EndTimeInput';
import { MobileContext } from '@wix/da-react-context/pkg/MobileContext';
import { getDiscountLimitations } from '../../../../selectors/studio/discounts';
import SubmitDiscountFormButton from './_partials/SubmitDiscountFormButton';
import MessageModal from '@wix/da-ds/pkg/modals/dsPresets/MessageModal';
import ConfirmationModal from '@wix/da-shared-react/pkg/Popup/ConfirmationModal';
import { Success as SuccessIllustration } from '@wix/da-ds/pkg/Icons/Confirmations/Success';
import { Alert as AlertIllustration } from '@wix/da-ds/pkg/Icons/Confirmations/Alert';
import BrandedButton from '@wix/da-ds/pkg/Buttons/BrandedButton';
import {
  PapiSellerDiscount,
  PapiStudioDiscountsCreate,
  PapiStudioDiscountsUpdate,
} from '@wix/da-papi-types';
import { DISCOUNT_SUBMIT_ERROR_STATUS_TOO_MANY } from './consts';

import s from './SellerDiscountModal.scss';

interface Props {
  discount?: PapiSellerDiscount;
  mode?: 'create' | 'edit' | 'duplicate';
  onRequestClose: () => void;
}

const ONE_HOUR_MS = 60 * 60 * 1000;
const ONE_WEEK_MS = 7 * 24 * 60 * 60 * 1000;

const SellerDiscountModal: FC<Props> = ({
  discount,
  mode = 'create',
  onRequestClose,
}) => {
  const { t } = useTranslation();
  const isMobile = useContext(MobileContext);
  const hasCore = useSelector(getCurrentUserHasCore);
  const [isShowingSuccessModal, setIsShowingSuccessModal] = useState(false);
  const [createdDiscountData, setCreatedDiscountData] =
    useState<DiscountDetailsData | null>(null);
  const [createdDiscount, setCreatedDiscount] =
    useState<PapiSellerDiscount | null>(null);
  const [isEditingCreatedDiscount, setIsEditingCreatedDiscount] =
    useState(false);
  const [isShowingErrorModal, setIsShowingErrorModal] = useState(false);
  const [errorTitle, setErrorTitle] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const formContext = useCreateForm({
    [DiscountFormField.Name]: discount?.name || '',
    [DiscountFormField.AppliesToCategory]: '',
    [DiscountFormField.AppliesToProduct]: discount?.appliesto || '',
    [DiscountFormField.DiscountValue]: discount?.amount?.percent?.toString(),
    [DiscountFormField.StartTimestamp]:
      (discount?.startDate && mode !== 'duplicate'
        ? new Date(discount?.startDate)
        : new Date(Date.now() + ONE_HOUR_MS)
      ).getTime() / 1000,
    [DiscountFormField.EndTimestamp]:
      (discount?.endDate && mode !== 'duplicate'
        ? new Date(discount?.endDate)
        : new Date(Date.now() + ONE_WEEK_MS)
      ).getTime() / 1000,
    [DiscountFormField.Audience]: discount?.audience || 'all',
  });
  const { errors, control } = formContext;

  const coreLevel = useSelector(getCoreLevel);
  const limitations = useSelector(getDiscountLimitations);
  const isShowingUpsellModal =
    mode !== 'edit' &&
    limitations?.numCampaignsAllowed &&
    limitations.numCampaignsRemaining === 0;

  const onSubmitSuccess = (
    response: PapiStudioDiscountsCreate | PapiStudioDiscountsUpdate
  ) => {
    if (mode === 'edit') {
      window.location.reload();
      return;
    }
    const values = formContext.getValues();
    setCreatedDiscountData({
      appliesToProduct: values[DiscountFormField.AppliesToProduct],
      discountValue: values[DiscountFormField.DiscountValue],
      audience: values[DiscountFormField.Audience],
      startTimestamp: values[DiscountFormField.StartTimestamp],
      endTimestamp: values[DiscountFormField.EndTimestamp],
    });
    if (response.discount) {
      setCreatedDiscount(response.discount);
    }
    setIsShowingSuccessModal(true);
  };

  const onSuccessModalClose = useCallback(() => {
    onRequestClose();
    window.location.href = Url.studio({
      section: 'discounts',
      discounts: 'all',
    });
  }, [onRequestClose]);

  const onEditCreatedDiscount = useCallback(() => {
    setIsShowingSuccessModal(false);
    setIsEditingCreatedDiscount(true);
  }, []);

  const onSubmitError = response => {
    if (response.errorCode === DISCOUNT_SUBMIT_ERROR_STATUS_TOO_MANY) {
      setErrorTitle(
        t(
          `studio.discountModal.form.error.tooMany.title${isMobile ? '.mobile' : ''}`
        )
      );
      setErrorMessage(
        t(
          `studio.discountModal.form.error.tooMany.message${isMobile ? '.mobile' : ''}`
        )
      );
    } else {
      setErrorTitle(response.errorDescription);
      setErrorMessage('');
    }
    setIsShowingErrorModal(true);
  };

  if (isEditingCreatedDiscount && createdDiscount) {
    return (
      <SellerDiscountModal
        discount={createdDiscount}
        mode="edit"
        onRequestClose={onRequestClose}
      />
    );
  }

  if (isShowingSuccessModal && createdDiscountData) {
    return (
      <MessageModal
        isOpen
        image={<SuccessIllustration />}
        variant="horizontal"
        title={t('studio.successModal.title')}
        message={<DiscountDetails data={createdDiscountData} />}
        contentClassName={s['success-modal-content']}
        onRequestClose={onSuccessModalClose}
        footerButtons={[
          <TextButton
            key="edit"
            size="large"
            variant="main"
            onClick={onEditCreatedDiscount}
          >
            {t('common.edit')}
          </TextButton>,
          <BrandedButton
            key="got-it"
            variant="brand-green"
            size="large"
            onClick={onSuccessModalClose}
          >
            {t('common.gotIt')}
          </BrandedButton>,
        ]}
      />
    );
  }

  if (isShowingErrorModal) {
    return (
      <MessageModal
        isOpen
        title={errorTitle}
        message={errorMessage}
        image={<AlertIllustration />}
        variant={isMobile ? 'vertical' : 'horizontal'}
        onRequestClose={onRequestClose}
        footerButtons={[
          <BrandedButton
            key="got-it"
            variant="brand-green"
            size="large"
            onClick={onRequestClose}
          >
            {t('common.gotIt')}
          </BrandedButton>,
        ]}
      />
    );
  }

  if (isShowingUpsellModal) {
    if (coreLevel === 'core_max') {
      return (
        <MessageModal
          isOpen
          title={t('studio.discountModal.upsell.coreMax.title')}
          message={t('studio.discountModal.upsell.coreMax.text')}
          image={<AlertIllustration />}
          variant={isMobile ? 'vertical' : 'horizontal'}
          onRequestClose={onRequestClose}
          footerButtons={[
            <BrandedButton
              key="got-it"
              variant="brand-green"
              size="large"
              onClick={onRequestClose}
            >
              {t('common.gotIt')}
            </BrandedButton>,
          ]}
        />
      );
    }
    return (
      <ConfirmationModal
        isOpen
        title={t('studio.discountModal.upsell.title', {
          allowed: limitations?.numCampaignsAllowed ?? 0,
        })}
        text={t('studio.discountModal.upsell.text', {
          allowed: limitations?.numCampaignsAllowed ?? 0,
        })}
        icon={<AlertIllustration />}
        confirmBtnLabel={t('common.upgradeCoreCTA')}
        confirmBtnType="core"
        onConfirm={() => window.location.assign(URLS.CORE_MEMBERSHIP)}
        onCancel={onRequestClose}
      />
    );
  }

  return (
    <FormProvider {...formContext}>
      <Modal
        isOpen
        onRequestClose={onRequestClose}
        className={s['root']}
        showHeaderBorder
        heading={
          <Flex direction="row" alignItems="center" gap={8}>
            <div>
              {t(
                `studio.discountModal.actionLabel.${mode === 'edit' ? 'edit' : 'create'}`
              )}
            </div>
            {!isMobile && <CoreExclusiveFeature withoutBuyCTA />}
          </Flex>
        }
        subheading={isMobile && <CoreExclusiveFeature withoutBuyCTA />}
        showFooterBorder
        footerRight={[
          <TextButton
            key="footer-cancel"
            size="large"
            variant="main"
            onClick={onRequestClose}
            type="button"
          >
            {t('common.cancel')}
          </TextButton>,
          hasCore && coreLevel !== 'core_access' ? (
            <SubmitDiscountFormButton
              label={t(
                `studio.discountModal.actionLabel.${mode === 'edit' ? 'edit' : 'create'}`
              )}
              discountId={mode === 'edit' ? discount?.discountId : undefined}
              onSuccess={onSubmitSuccess}
              onError={onSubmitError}
            />
          ) : (
            <BuyCoreBrandedButton size="large">
              {t('studio.discountModal.upgradeToCore.label')}
            </BuyCoreBrandedButton>
          ),
        ]}
        withScrollableContentWrapper={isMobile}
        contentClassName={s['inner-layout']}
      >
        <div className={s['form-container']}>
          <Controller
            name={DiscountFormField.Name}
            control={control}
            render={({ onChange, value, name }) => {
              const error = errors[name];
              return (
                <TextInput
                  id={DiscountFormField.Name}
                  name={DiscountFormField.Name}
                  className={s['form-input']}
                  label={t('studio.discountModal.form.nameInput.label')}
                  subLabel={t('studio.discountModal.form.nameInput.subLabel')}
                  autoFocus
                  maxLength={60}
                  value={value}
                  isRequired
                  validationResult={error ? 'error' : undefined}
                  details={error?.message}
                  onChange={onChange}
                />
              );
            }}
          />
          <AppliesToSelector inputClassName={s['form-input']} />
          <DiscountSelector />
          <div className={s['time-selection-area']}>
            <StartTimeInput
              isEditingExistingDiscount={mode === 'edit'}
              className={c(s['form-input'], s['ts-input'])}
            />
            <EndTimeInput className={c(s['form-input'], s['ts-input'])} />
          </div>
          <AudienceSelector className={s['form-input']} />
        </div>
      </Modal>
    </FormProvider>
  );
};
SellerDiscountModal.displayName = 'SellerDiscountModal';

export default SellerDiscountModal;
